import { computed } from '@angular/core';
import type {
  ImageDeliverable,
  ImageDeliverableTextMetadata,
} from '@bizzdesign/modeler/models';
import { toImageClassificationFields } from '@bizzdesign/modeler/utils-canvazz';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { withImageReducer } from './image.reducer';
import { trimImageCache } from './image.store.utils';

export type ImageState = {
  images: ImageDeliverable[];
  evictableImageIds: string[];
};

const initialState: ImageState = {
  images: [],
  evictableImageIds: [],
};

export const ImageLocalStore = signalStore(
  { protectedState: false },
  withState<ImageState>(initialState),
  withComputed((store) => ({
    imageIdToSrcMap: computed(
      () => new Map(store.images().map(({ id, imageSrc }) => [id, imageSrc])),
    ),
    imageIdToTextMetadataMap: computed(
      () =>
        new Map<string, ImageDeliverableTextMetadata>(
          store
            .images()
            .flatMap(
              ({
                id,
                description,
                alt,
                ...classificationFields
              }): [string, ImageDeliverableTextMetadata][] =>
                description === undefined || alt === undefined
                  ? []
                  : [
                      [
                        id,
                        {
                          description,
                          alt,
                          ...toImageClassificationFields(classificationFields),
                        },
                      ],
                    ],
            ),
        ),
    ),
  })),
  withMethods((store) => ({
    clearCache(): void {
      patchState(store, { images: [], evictableImageIds: [] });
    },
    markImageCacheEntriesForEviction(imageIds: string[]): void {
      const imageIdsToMark = new Set(imageIds);
      const evictableImageIds = [
        ...store.evictableImageIds().filter((id) => !imageIdsToMark.has(id)),
        ...imageIdsToMark,
      ];
      patchState(store, trimImageCache(store.images(), evictableImageIds));
    },
    restoreImageCacheEntries(imageIds: string[]): void {
      const restoredImageIds = new Set(imageIds);
      patchState(store, {
        evictableImageIds: store
          .evictableImageIds()
          .filter((id) => !restoredImageIds.has(id)),
      });
    },
  })),
  withImageReducer(),
);

export type ImageLocalStore = InstanceType<typeof ImageLocalStore>;
